import React, { useState } from 'react';
import { ChevronDown, Quote, Star, Facebook, ExternalLink } from 'lucide-react';
import { faqs as defFaqs, testimonials as defTestimonials, partners, socials } from '../mock/data';
import { useSiteSettings } from '../context/SiteSettingsContext';

export const FAQSection = () => {
  const [open, setOpen] = useState(0);
  const { getContent } = useSiteSettings();
  const faqs = getContent('faqs', defFaqs);
  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">FAQ</span><span className="h-px w-10 bg-[#c8102e]" /></div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black">Frequently Asked Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, idx) => (
            <div key={idx} className="border border-neutral-200 bg-white overflow-hidden">
              <button onClick={() => setOpen(open === idx ? -1 : idx)} className="w-full flex items-center justify-between p-5 text-left hover:bg-neutral-50 transition-colors">
                <span className="font-semibold text-black pr-4">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-[#c8102e] transition-transform shrink-0 ${open === idx ? 'rotate-180' : ''}`} />
              </button>
              {open === idx && (<div className="px-5 pb-5 text-neutral-600 leading-relaxed border-t border-neutral-100 pt-4">{f.a}</div>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const TestimonialsSection = () => {
  const { getContent } = useSiteSettings();
  const testimonials = getContent('testimonials', defTestimonials);
  return (
  <section className="py-24 bg-neutral-50">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-14">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Testimonials</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black">What our customers say</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {testimonials.map((t) => (
          <div key={t.name} className="bg-white p-7 border-t-4 border-[#c8102e] shadow-sm hover:shadow-xl transition-shadow">
            <Quote className="w-8 h-8 text-[#c8102e] mb-4" />
            <p className="text-neutral-700 text-sm leading-relaxed mb-6 italic">“{t.text}”</p>
            <div className="flex items-center gap-1 mb-3">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#c8102e] text-[#c8102e]" />)}
            </div>
            <div className="font-serif-display font-bold text-black text-lg">{t.name}</div>
            <div className="text-xs text-neutral-500 tracking-wide">{t.location}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
  );
};

export const PartnersSection = () => (
  <section className="py-16 bg-white border-t border-neutral-100">
    <div className="max-w-7xl mx-auto px-6">
      <div className="text-center mb-10">
        <div className="flex items-center gap-3 justify-center mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Trusted Partners</span><span className="h-px w-10 bg-[#c8102e]" /></div>
        <h3 className="font-serif-display text-2xl font-bold text-black">Working with the world’s leading logistics providers</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6 items-center">
        {partners.map((p) => (
          <div key={p} className="text-center py-5 px-2 border border-neutral-100 hover:border-[#c8102e] hover:text-[#c8102e] transition-colors font-serif-display font-bold text-neutral-500">{p}</div>
        ))}
      </div>
    </div>
  </section>
);

export const FacebookFeed = () => {
  const fbSocial = socials.find((s) => s.icon === 'facebook');
  const pageUrl = 'https://www.facebook.com/LankaExpressEU';
  const embedSrc = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(pageUrl)}&tabs=timeline&width=500&height=700&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false&locale=en_GB`;
  return (
    <section className="py-24 bg-black text-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
        <div className="lg:sticky lg:top-32">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Live From Facebook</span></div>
          <h2 className="font-serif-display text-4xl md:text-5xl font-bold mb-6 leading-[1.05]">Latest offers &<br /><em className="text-[#c8102e] not-italic">updates</em> from our team</h2>
          <p className="text-white/70 mb-8 leading-relaxed text-[15px] max-w-md">
            Follow our official Facebook page for real-time updates on new routes, seasonal offers, gift-service promotions and behind-the-scenes stories — straight from our team.
          </p>
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[#c8102e] animate-pulse" />
              <span className="text-sm text-white/80">Auto-updated from our Facebook timeline</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[#c8102e]" />
              <span className="text-sm text-white/80">Exclusive offers announced first on Facebook</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-1.5 rounded-full bg-[#c8102e]" />
              <span className="text-sm text-white/80">Join over 20,000 followers worldwide</span>
            </div>
          </div>
          <a href={fbSocial?.url || pageUrl} target="_blank" rel="noreferrer" className="group inline-flex items-center gap-3 bg-[#c8102e] hover:bg-white hover:text-black text-white font-semibold h-12 px-8 text-[13px] tracking-[0.15em] uppercase transition-colors">
            <Facebook className="w-4 h-4" /> Follow on Facebook <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <div className="bg-white p-2 shadow-2xl">
          <iframe
            title="Lanka Express Facebook feed"
            src={embedSrc}
            width="100%"
            height="700"
            style={{ border: 'none', overflow: 'hidden' }}
            scrolling="no"
            frameBorder="0"
            allowFullScreen
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          />
        </div>
      </div>
    </section>
  );
};
